import { useMyAccess } from '@/lib/useMyAccess';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GroupedLayout } from '@/components/layout/GroupedLayout';
import { api } from '@/lib/api';
import { teacherGroups } from './nav';
import { RemarksPage } from '@/features/owner/pages/RemarksPage';
import { LessonsStudio } from '@/features/shared/teaching/LessonsStudio';
import { LiveClassesPage } from '@/features/shared/teaching/LiveClassesPage';
import { LessonNotesPage } from '@/features/shared/teaching/LessonNotesPage';
import { StaffMessagesPage } from '@/features/shared/support/StaffMessagesPage';
import { ScoreEntryPage } from '@/features/owner/pages/ScoreEntryPage';
import { OwnerImportScoresPage } from '@/features/owner/pages/OwnerImportScoresPage';
import { QuestionBanksPage } from '@/features/owner/pages/QuestionBanksPage';
import { OwnerCbtExamsListPage } from '@/features/owner/pages/OwnerCbtExamsListPage';
import { OwnerCbtCreatePage } from '@/features/owner/pages/OwnerCbtCreatePage';
import { OwnerCbtExamDetailPage } from '@/features/owner/pages/OwnerCbtExamDetailPage';
import { AttendancePage } from '@/features/owner/pages/AttendancePage';
import { AccountPage } from '@/features/auth/pages/AccountPage';
import { NotificationsCenter } from '@/features/auth/pages/NotificationsCenter';
import { DashboardPage } from './pages/DashboardPage';
import { ClassesPage } from './pages/ClassesPage';
import { TimetablePage } from './pages/TimetablePage';
import { AssignmentsPage } from './pages/AssignmentsPage';
import { ClassResultsPage } from './pages/ClassResultsPage';
import { MyPayPage } from './pages/MyPayPage';
import { ClockInPage } from './pages/ClockInPage';
import { CalendarPage } from './pages/CalendarPage';

/** The teacher's shell: the owner's dropdown menu with live counts (work to grade, unread messages). */
function TeacherLayout() {
  const h = useQuery({ queryKey: ['teacher-home'], queryFn: () => api.get('/teacher/home').then((r) => (r.data && typeof r.data === 'object' && 'success' in r.data ? r.data.data : r.data) ?? null), refetchInterval: 120_000, retry: false });
  const { hidden, customRole } = useMyAccess();
  return (
    <GroupedLayout base="/teacher" roleLabel={customRole ?? "Teacher"} groups={teacherGroups} padded hidden={hidden}
      ownPadding={['score-entry', 'import-scores', 'question-banks', 'cbt-exams', 'attendance', 'remarks', 'lessons', 'live-classes', 'lesson-notes', 'messages']}
      badges={{ assignments: h.data?.to_grade }} />
  );
}

export function teacherRoutes() {
  return (
    <Routes>
      <Route element={<TeacherLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="classes" element={<ClassesPage />} />
        <Route path="timetable" element={<TimetablePage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="lessons" element={<LessonsStudio />} />
        <Route path="live-classes" element={<LiveClassesPage />} />
        <Route path="assignments" element={<AssignmentsPage />} />

        <Route path="question-banks" element={<QuestionBanksPage />} />
        <Route path="question-bank" element={<Navigate to="/teacher/question-banks" replace />} />
        <Route path="cbt-exams" element={<OwnerCbtExamsListPage />} />
        <Route path="cbt-exams/new" element={<OwnerCbtCreatePage />} />
        <Route path="cbt-exams/:id" element={<OwnerCbtExamDetailPage />} />
        <Route path="cbt-create" element={<Navigate to="/teacher/cbt-exams" replace />} />
        <Route path="cbt-push" element={<Navigate to="/teacher/cbt-exams" replace />} />
        <Route path="live-monitor" element={<Navigate to="/teacher/cbt-exams" replace />} />
        <Route path="cbt-results" element={<Navigate to="/teacher/cbt-exams" replace />} />

        <Route path="score-entry" element={<ScoreEntryPage />} />
        <Route path="import-scores" element={<OwnerImportScoresPage />} />
        <Route path="remarks" element={<RemarksPage />} />
        <Route path="class-results" element={<ClassResultsPage />} />

        <Route path="lesson-notes" element={<LessonNotesPage />} />
        <Route path="messages" element={<StaffMessagesPage />} />
        <Route path="calendar" element={<CalendarPage />} />

        <Route path="clock-in" element={<ClockInPage />} />
        <Route path="my-pay" element={<MyPayPage />} />
        <Route path="notifications" element={<NotificationsCenter />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
  );
}
